import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { connectionFor, detailOf, pathFor, stepStatus } from './steps'

/**
 * Mirrors `SenderRequest.from_name` in `openapi.json` (`maxLength: 200`). Bounded
 * here as well as on the server so the refusal is immediate and attached to the
 * control, rather than a "Request validation failed" naming no field (WEB-14).
 */
const FROM_NAME_MAX = 200

const PROVIDER = 'sender_identity'

interface DnsRow {
  kind?: unknown
  name?: unknown
  value?: unknown
  verified?: unknown
}

/**
 * Step 4 — S-D2's `s4`.
 *
 * The DNS records come back from the provider and are rendered with a copy
 * button each, because "outreach silently landing in spam" is the most expensive
 * failure in this product and it is entirely preventable at this screen. They
 * are read defensively out of `detail` — the shape belongs to S-B18, and a row
 * missing a field should drop that row rather than crash the step somebody is
 * standing on.
 *
 * Nobody has to wait here. DNS takes up to an hour to propagate, the server
 * re-checks, and the copy says so — a wizard step that asked a tenant to sit and
 * refresh would be a wizard step most people abandon.
 */
export function SenderStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'sender')
  const [fromName, setFromName] = useState('')
  const [fromEmail, setFromEmail] = useState(connection?.account_label ?? '')

  const save = useMutation((body: { from_name: string; from_email: string }) =>
    call(() => api.POST('/api/v1/onboarding/sender', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({ from_name: fromName.trim(), from_email: fromEmail.trim() })
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  const records = dnsRecords(detailOf(connection).dns)

  return (
    <form
      className="app-wiz"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <WizardChrome current="sender" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">Who are emails from?</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              Replies come back to this address and land in your unified inbox.
            </p>
          </div>

          <InlineError failure={save.state.failure} />

          <div className="ar-grid ar-grid--2">
            <TextField
              label="From name"
              required
              autoFocus
              maxLength={FROM_NAME_MAX}
              placeholder="Priya at Kavya Interiors"
              value={fromName}
              onChange={(event) => {
                setFromName(event.target.value)
                save.reset()
              }}
            />
            <TextField
              label="From address"
              type="email"
              required
              placeholder="priya@kavyainteriors.in"
              value={fromEmail}
              onChange={(event) => {
                setFromEmail(event.target.value)
                save.reset()
              }}
            />
          </div>

          {records.length > 0 ? (
            <div className="ar-panel ar-stack ar-stack--tight">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <strong>Domain authentication</strong>
                <span className="ar-badge ar-badge--neutral">
                  {records.filter((row) => row.verified).length} of {records.length} verified
                </span>
              </div>
              <div className="ar-table-wrap">
                <table className="ar-table" style={{ background: 'transparent' }}>
                  <tbody>
                    {records.map((row) => (
                      <tr key={`${row.kind}-${row.name}`}>
                        <td>
                          <span
                            className={`ar-badge ar-badge--${row.verified ? 'success' : 'warning'}`}
                          >
                            {row.verified ? '✓' : '!'} {row.kind}
                          </span>
                        </td>
                        <td
                          className="ar-mono"
                          style={{
                            fontSize: 'var(--ar-text-caption-size)',
                            wordBreak: 'break-all',
                          }}
                        >
                          {row.name} → {row.value}
                        </td>
                        <td className="ar-num">
                          <CopyButton value={row.value} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p
                className="ar-muted"
                style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                DNS changes take up to an hour. We re-check automatically and email you when
                it&rsquo;s done — you don&rsquo;t have to wait here.
              </p>
            </div>
          ) : null}

          {step?.status === 'failed' && detailOf(step).message ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>We could not set up a sending address</strong>
                {String(detailOf(step).message)}
              </div>
            </div>
          ) : null}
        </div>

        <WizardFooter current="sender">
          <Button tone="primary" type="submit" busy={save.pending} busyLabel="Setting up">
            Continue
          </Button>
        </WizardFooter>
      </section>
    </form>
  )
}

interface DnsRecord {
  kind: string
  name: string
  value: string
  verified: boolean
}

/** Read the provider's DNS rows defensively — S-B18 owns the shape, not this file. */
function dnsRecords(value: unknown): DnsRecord[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row: DnsRow) => {
    if (typeof row.kind !== 'string' || typeof row.name !== 'string') return []
    if (typeof row.value !== 'string') return []
    return [{ kind: row.kind, name: row.name, value: row.value, verified: row.verified === true }]
  })
}

/**
 * Copy to the clipboard, saying so.
 *
 * `navigator.clipboard` is absent in insecure contexts and in jsdom, so the
 * failure path leaves the button unchanged rather than throwing — the record is
 * on screen and can still be selected by hand.
 */
function CopyButton({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false)
  // WEB-13. The revert was scheduled with no cleanup, so pressing *Continue*
  // inside the two seconds set state on an unmounted component. Held in a ref
  // rather than in state: nothing renders from it, and a state update here
  // would restart the very timer it is trying to track.
  const revert = useRef<number | undefined>(undefined)

  useEffect(
    () => () => {
      if (revert.current !== undefined) window.clearTimeout(revert.current)
    },
    [],
  )

  return (
    <Button
      tone="quiet"
      size="sm"
      onClick={() => {
        // Typed as always present, and it is not: absent in an insecure context
        // and in jsdom. The cast is what lets the honest check compile.
        const clipboard = navigator.clipboard as Clipboard | undefined
        void clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true)
            // Copying a second record inside the window would otherwise leave
            // the first timer to clear the second one's "Copied" early.
            if (revert.current !== undefined) window.clearTimeout(revert.current)
            revert.current = window.setTimeout(() => {
              setCopied(false)
            }, 2000)
          })
          .catch(() => {
            /* No clipboard permission. The value is visible and selectable. */
          })
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}
